// V1 source: customer/midas/components/MidasDragHandle.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 drag handle — 4px warm-stone vertical bar between chat and dock with
// 3-dot indicator on hover/drag. Logic copied verbatim from V1: drag math,
// MIN_PANEL_WIDTH = 360, MAX_PANEL_PERCENT = 0.66, dispatch to
// setArtifactPanelWidth. Tailwind for all chrome.
//
// Per Paper artboard `MIDAS V2 — Phase 2 — Dock — Spec` (panel G):
// "4px warm-stone vertical strip between Reading Column and Dock. Three 2px
//  graphite dots stacked at center (VS-Code hover affordance). At max →
//  header shows `MAX · 66% viewport` mono caption."

import React, { useCallback, useEffect, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { setArtifactPanelWidth } from "../../midasPageSlice";

// ===================================================================
// Constants — verbatim from V1
// ===================================================================

const MIN_PANEL_WIDTH = 360;
const MAX_PANEL_PERCENT = 0.66; // ~2/3 viewport (Claude Desktop style)

// ===================================================================
// MidasDragHandleV2
// ===================================================================

const MidasDragHandleV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Drag math — identical to V1
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const vw = window.innerWidth;
      const maxWidth = Math.floor(vw * MAX_PANEL_PERCENT);
      // Panel width = distance from right edge of viewport to cursor
      const newWidth = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(maxWidth, vw - e.clientX)
      );
      dispatch(setArtifactPanelWidth(newWidth));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dispatch]);

  const showDots = isHovered || isDragging;

  return (
    <div
      role="separator"
      aria-label={translate("midas.aria.dragHandle") || "Drag to resize artifact panel"}
      aria-orientation="vertical"
      tabIndex={0}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={[
        "relative flex w-[4px] flex-shrink-0 items-center justify-center cursor-col-resize",
        isDragging
          ? "bg-midas-terracotta"
          : "bg-midas-warm-stone hover:bg-midas-stone",
        isDragging ? "" : "transition-colors duration-150",
      ].join(" ")}
    >
      {/* 3-dot indicator (VS-Code style) — visible on hover or while dragging */}
      {showDots && (
        <div className="pointer-events-none absolute flex flex-col gap-[3px]">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={[
                "h-[2px] w-[2px] rounded-full",
                isDragging ? "bg-white" : "bg-midas-graphite",
              ].join(" ")}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MidasDragHandleV2;
